import React from 'react';
import { Skeleton, Table, useMediaQueryContext } from '@fourthwall/components';
import { formatSubscriptionBytes, getSubscriptionSamplesCreditGrant, useSubscription, useSubscriptionStorageLimits, useTopSubscriptionPlan, } from '@modules/Dashboard/modules/Subscriptions';
import { useDefaultSubscriptionPlanQuery } from '@modules/Dashboard/modules/Subscriptions/queries/useDefaultSubscriptionPlanQuery';
import * as S from './SubscriptionPerksTable.styled';
const getLimitLabel = (value) => {
    if (value === -1)
        return 'Unlimited';
    return value;
};
export const SubscriptionPerksTable = () => {
    const media = useMediaQueryContext();
    const { hasActiveProSubscription, activeSubscription } = useSubscription();
    const { digitalProductsStorage, productDesignerStorage } = useSubscriptionStorageLimits();
    const { topSubscriptionPlan } = useTopSubscriptionPlan(hasActiveProSubscription && activeSubscription?.plan.recurringPeriod
        ? { recurringPeriod: activeSubscription?.plan.recurringPeriod }
        : undefined);
    const { defaultSubscriptionPlanQuery } = useDefaultSubscriptionPlanQuery();
    const isCustom = activeSubscription?.plan.custom;
    const freePlan = defaultSubscriptionPlanQuery.data;
    const showProPlanFeatures = !!topSubscriptionPlan && !isCustom;
    const showFreePlanFeatures = defaultSubscriptionPlanQuery.isFetched && !isCustom;
    if (!freePlan || !topSubscriptionPlan || !activeSubscription)
        return React.createElement(SubscriptionsPerksTableSkeleton, null);
    const tableHeaders = [
        {
            id: 'feature',
            label: 'Feature',
            render: (data) => React.createElement(S.Cell, null, data.feature),
            width: 2,
        },
        {
            id: 'freePlan',
            label: hasActiveProSubscription ? 'Free' : 'Free (your plan)',
            alignment: 'right',
            hidden: !showFreePlanFeatures,
            render: (data) => React.createElement(S.Cell, { type: "light" }, data.freePlan),
        },
        {
            id: 'planPro',
            label: hasActiveProSubscription ? 'Pro (your plan)' : 'Pro',
            alignment: 'right',
            hidden: !showProPlanFeatures,
            render: (data) => React.createElement(S.Cell, { type: "strong" }, data.proPlan),
        },
        {
            id: 'currentPlan',
            label: 'Your custom plan',
            alignment: 'right',
            hidden: !isCustom,
            render: (data) => React.createElement(S.Cell, { type: "strong" }, data.currentPlan),
        },
    ];
    const getDomainFeatureLabel = (domainsLimit) => (domainsLimit === 0 ? 'No' : 'Yes');
    const separatorType = media.tablet ? 'full' : 'short';
    const featuresData = [
        {
            feature: 'Free sample credit',
            freePlan: getSubscriptionSamplesCreditGrant({
                subscriptionPlan: freePlan,
                separatorType,
            }),
            proPlan: getSubscriptionSamplesCreditGrant({
                subscriptionPlan: topSubscriptionPlan,
                separatorType,
            }),
            currentPlan: getSubscriptionSamplesCreditGrant({
                subscriptionPlan: activeSubscription.plan,
                separatorType,
            }),
        },
        {
            feature: 'Free .store domain',
            freePlan: getDomainFeatureLabel(freePlan.domainsLimit),
            proPlan: getDomainFeatureLabel(topSubscriptionPlan.domainsLimit),
            currentPlan: getDomainFeatureLabel(activeSubscription.plan.domainsLimit),
        },
        {
            feature: 'Product listings',
            freePlan: `${getLimitLabel(freePlan.offersLimit)}`,
            proPlan: `${getLimitLabel(topSubscriptionPlan.offersLimit)}`,
            currentPlan: `${getLimitLabel(activeSubscription.plan.offersLimit)}`,
        },
        {
            feature: 'Team members',
            freePlan: `${getLimitLabel(freePlan.membersLimit)}`,
            proPlan: `${getLimitLabel(topSubscriptionPlan.membersLimit)}`,
            currentPlan: `${getLimitLabel(activeSubscription.plan.membersLimit)}`,
        },
        {
            feature: 'Digital products fee',
            freePlan: `${freePlan.digitalProductsFeePercent}%`,
            proPlan: `${topSubscriptionPlan.digitalProductsFeePercent}%`,
            currentPlan: `${activeSubscription.plan.digitalProductsFeePercent}%`,
        },
        {
            enabled: digitalProductsStorage.enabled,
            feature: 'Digital products file storage limit',
            freePlan: formatSubscriptionBytes(freePlan.digitalProductsStorageLimitBytes),
            proPlan: formatSubscriptionBytes(topSubscriptionPlan.digitalProductsStorageLimitBytes),
            currentPlan: formatSubscriptionBytes(activeSubscription.plan.digitalProductsStorageLimitBytes),
        },
        {
            enabled: productDesignerStorage.enabled,
            feature: 'Product designer file storage limit',
            freePlan: formatSubscriptionBytes(freePlan.productDesignerArtworkStorageLimitBytes),
            proPlan: formatSubscriptionBytes(topSubscriptionPlan.productDesignerArtworkStorageLimitBytes),
            currentPlan: formatSubscriptionBytes(activeSubscription.plan.productDesignerArtworkStorageLimitBytes),
        },
    ];
    return (React.createElement(S.Container, null,
        React.createElement(Table, { compact: true, data: featuresData.filter((feature) => feature.enabled !== false), headers: tableHeaders.filter((header) => !header.hidden), minWidth: 0 })));
};
const SubscriptionsPerksTableSkeleton = () => {
    return (React.createElement(Skeleton, { gap: 16 }, [...Array(8)].map((_, index) => (React.createElement(Skeleton.Shape, { key: index, height: 24, width: "100%" })))));
};
